import TimelinePenerimaan from "@/components/penerimaan/TimelinePenerimaan";
import SectionHeader from "@/components/SectionHeader";
import Stepsbar from "@/components/Stepsbar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import useDataStep from "@/hooks/useDataStep";
import { useMultistepForm } from "@/hooks/useMultistepForm";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import api from "@/lib/axios";
import { formatDateIndonesia } from "@/lib/utils";
import { getDataStep, getListPrestasi } from "@/services/getData";
import { BiodataSate, DataSiswa, Step } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  ArrowRight,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  DoorClosed,
  Loader2,
  Megaphone,
  MessageCircle,
  Save,
} from "lucide-react";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import toast from "react-hot-toast";
import { Link, Navigate, useNavigate } from "react-router-dom";
import Biodata from "./partial/biodata/Biodata";
import KartuPeserta from "./partial/kartu-peserta/KartuPeserta";
import NilaiRapor from "./partial/nilai-rapor/NilaiRapor";
import Portofolio from "./partial/portofolio/Portofolio";
import Prestasi from "./partial/prestasi/Prestasi";
import Prodi from "./partial/prodi/Prodi";
import Resume from "./partial/resume/Resume";
import UnggahBerkas from "./partial/unggah-berkas/UnggahBerkas";
import { web } from "@/config/System";

const saveBiodata = async (form: FormData) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/biodata/update",
    form,
    { withCredentials: true },
  );
  return response.data;
};

export default function PenerimaanSekolahLab() {
  const navigate = useNavigate();
  const [sessionPeriode, setSessionPeriode] = useSessionStorage(
    "sessionPeriode",
    {
      id_sys_periode: "",
    },
  );

  const { isSudahFinalisasi, isValidasiPendaftaran } = useDataStep();
  const {
    isPendingPeriode,
    dataPeriode,
    timePassed,
    timePassedActive,
    dataPeriodeActive,
  } = useTimePeriode();
  const { data: dataStep } = useQuery({
    queryKey: ["dataStep", sessionPeriode?.id_sys_periode],
    queryFn: () => getDataStep(sessionPeriode?.id_sys_periode || ""),
  });

  const { data: listDataPrestasi, isFetched: isFetchedListDataPrestasi } =
    useQuery({
      queryKey: ["listPrestasi"],
      queryFn: () => getListPrestasi(sessionPeriode?.id_sys_periode || ""),
    });

  const idDataPendaftaran = dataStep?.id_data_pendaftaran;
  const [isPendingSteps, setisPendingSteps] = useState(false);
  const [steps, setSteps] = useState<Step[]>([
    {
      number: 1,
      text: "Biodata",
      link: "#",
      status: "active",
      tabStatus: "active",
    },
    { number: null, text: null, link: null, status: "nonactive" },
    {
      number: 2,
      text: "Prodi",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    { number: null, text: null, link: null, status: "nonactive" },
    {
      number: 3,
      text: "Berkas",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 4,
      text: "Nilai Raport",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 5,
      text: "Prestasi",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 6,
      text: "Portofolio",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 7,
      text: "Rangkuman Pendaftaran",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 8,
      text: "Pembayaran & Kartu Peserta",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
  ]);
  const [currentIndexDisable, setCurrentIndexDisable] = useState(1);

  const [formBiodata, setFormBiodata] = useState<BiodataSate>({
    id_sys_periode: sessionPeriode?.id_sys_periode || "",
    id_data_siswa: 0,
    id_ref_sekolah: "",
    id_ref_agama: "",
    id_ref_negara: "",
    id_ref_alat_transportasi: "",
    id_ref_jenis_tinggal: "",
    nisn: "",
    nama_lengkap: "",
    handphone: "",
    npwp: "",
    tanggal_lahir: "",
    tempat_lahir: "",
    jenis_kelamin: "L",
    berat_badan: 0,
    tinggi_badan: 0,
    ukuran_baju: "",
    jalan: "",
    rt: 0,
    rw: 0,
    kode_pos: "",
    kelurahan: "",
    provinsi: "",
    kabupaten: "",
    kecamatan: "",
    nama_panti_asuhan: "",
    kebutuhan_khusus: [] as string[],
    pilihan_lokasi_kuliah: "",
    is_alamat_sesuai_domisili: null,
    jalan_domisili: "",
    kelurahan_domisili: "",
    kecamatan_domisili: "",
    kabupaten_domisili: "",
    provinsi_domisili: "",

    kontak_darurat_hubungan: "",
    kontak_darurat_nama: "",
    kontak_darurat_hp: "",
  });
  const [file, setFile] = useState<File | null>(null);

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingSaveBiodata,
    error,
  } = useMutation({
    mutationFn: saveBiodata,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataStep"],
      });
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const {
    currentStep,
    firstStep,
    lastStep,
    backStep,
    goToStep,
    currentIndex,
    currentStepNumber,
  } = useMultistepForm([
    <Biodata
      form={formBiodata}
      setForm={setFormBiodata}
      file={file}
      setFile={setFile}
      error={error}
    />,
    <Prodi idDataPendaftaran={idDataPendaftaran} />,
    <UnggahBerkas idDataPendaftaran={idDataPendaftaran} />,
    <NilaiRapor idDataPendaftaran={idDataPendaftaran} />,
    <Prestasi idDataPendaftaran={idDataPendaftaran} />,
    <Portofolio idDataPendaftaran={idDataPendaftaran} />,
    <Resume />,
    <KartuPeserta />,
  ]);

  const handleSaveBiodata = async () => {
    const formData = new FormData();

    Object.keys(formBiodata).forEach((key) => {
      const value = formBiodata[key as keyof DataSiswa];
      if (Array.isArray(value)) {
        // Handle array (kebutuhan_khusus)
        value.forEach((item, index) => {
          if (item !== null && item !== undefined) {
            formData.append(`${key}[${index}]`, String(item)); // Konversi ke string menggunakan String()
          }
        });
      } else {
        // Handle nilai non-array
        if (value !== null && value !== undefined) {
          formData.append(key, String(value)); // Konversi ke string menggunakan String()
        }
      }
    });
    if (file) formData.append("foto", file);

    mutate(formData);
  };

  const handleNextStep = () => {
    switch (currentStepNumber) {
      case 1:
        if (dataStep?.data_diri === 1) {
          goToStep(2);
        }
        break;
      case 2:
        if (dataStep?.data_pilihan_prodi === 1) {
          goToStep(3);
        }
        break;
      case 3:
        if (dataStep?.data_berkas === 1) {
          goToStep(4);
        }
        break;
      case 4:
        if (dataStep?.data_rapor === 1) {
          goToStep(5);
        }
        break;
      case 5:
        if (dataStep?.data_prestasi === 1) {
          goToStep(6);
        }
        break;
      case 6:
        if (dataStep?.data_portofolio === 1) {
          goToStep(7);
        }
        break;

      case 7:
        if (dataStep?.is_resume_pendaftaran === 1) {
          goToStep(8);
        }
        break;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    setisPendingSteps(true);

    if (!dataStep) return;

    setSteps((prev) => {
      const updatedSteps = [...prev];

      // Helper function untuk update status connector (null steps)
      const updateConnectorStatus = (index: number) => {
        if (index <= 0 || index >= updatedSteps.length) return;

        const prevStep = updatedSteps[index - 1];
        if (updatedSteps[index].number === null) {
          updatedSteps[index].status =
            prevStep.status === "completed" ? "completed" : "nonactive";
        }
      };

      // Update main steps status
      updatedSteps.forEach((step, index) => {
        if (!step.text) return; // Skip connector steps

        switch (step.text) {
          case "Biodata":
            if (dataStep?.data_diri === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(2);
              setCurrentIndexDisable(2);
            }
            break;

          case "Prodi":
            if (dataStep?.data_pilihan_prodi === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(3);
              setCurrentIndexDisable(3);
            }
            break;
          case "Berkas":
            if (dataStep?.data_berkas === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(4);
              setCurrentIndexDisable(4);
            }
            break;
          case "Nilai Raport":
            if (dataStep?.data_rapor === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(5);
              setCurrentIndexDisable(5);
            }
            break;
          case "Prestasi":
            if (dataStep?.data_prestasi === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              setCurrentIndexDisable(6);

              // JIKA TIDAK MEMILIKI PRESTASI LANGSUNG KE NEXT STEP
              if (
                isFetchedListDataPrestasi &&
                listDataPrestasi?.length == 0 &&
                currentStepNumber == 5
              ) {
                goToStep(6);
              }
            }
            break;
          case "Portofolio":
            if (dataStep?.data_portofolio === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(7);
              setCurrentIndexDisable(7);
            }
            break;
          case "Rangkuman Pendaftaran":
            if (dataStep?.is_resume_pendaftaran === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(8);
              setCurrentIndexDisable(8);
            }
            break;
          case "Pembayaran & Kartu Peserta":
            if (dataStep?.valid_is_pendaftaran === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
            }
            break;
        }
      });

      // Update connector steps
      updatedSteps.forEach((_, index) => {
        updateConnectorStatus(index);
      });

      return updatedSteps;
    });

    setisPendingSteps(false);
  }, [goToStep, dataStep]);

  useEffect(() => {
    setSteps((prev) =>
      prev.map((item) => {
        if (item.number != currentStepNumber)
          return { ...item, tabStatus: "nonactive" };
        return { ...item, tabStatus: "active" };
      }),
    );
  }, [currentStepNumber]);

  useEffect(() => {
    if (timePassed.waktu_pengumuman_kelulusan) {
      flushSync(() => {
        setSessionPeriode({
          id_sys_periode: dataPeriode?.id_sys_periode || "",
        });
      });
      navigate(`/pengumuman-kelulusan`);
    }
    if (timePassed.waktu_pengumuman) {
      flushSync(() => {
        setSessionPeriode({
          id_sys_periode: dataPeriode?.id_sys_periode || "",
        });
      });
      navigate(`/pengumuman`);
    }
  }, [timePassed, navigate, sessionPeriode?.id_sys_periode]);

  const isDisableInput =
    isSudahFinalisasi ||
    isValidasiPendaftaran ||
    timePassedActive?.waktu_selesai_daftar_valid_selesai;

  if (
    dataPeriode &&
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug !== "sekolahlab"
  ) {
    return <Navigate to="/dashboard" />;
  }

  const SHOW_TIMELINE =
    currentStepNumber !== 2 &&
    currentStepNumber !== 4 &&
    currentStepNumber !== 5 &&
    currentStepNumber !== 6 &&
    currentStepNumber !== 7 &&
    currentStepNumber !== 8;

  return (
    <main className="relative min-h-screen">
      <SectionHeader
        title={`Penerimaan - ${
          isPendingPeriode
            ? "Loading..."
            : dataPeriode?.jalur_jenis?.nama_jalur_jenis
        }`}
      />
      {/* --- BAGIAN 1: STATUS PENDAFTARAN DITUTUP (Kondisional) --- */}
      {timePassedActive?.waktu_selesai_daftar_valid_selesai && (
        <Alert className="mb-6 border-l-4 border-l-rose-500 bg-rose-50 dark:bg-rose-950/20 border-t border-r border-b border-rose-200 dark:border-rose-800 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-white dark:bg-rose-900/50 rounded-full shadow-sm mt-1 shrink-0">
              <DoorClosed className="h-6 w-6 text-rose-600 dark:text-rose-400" />
            </div>

            <div className="flex-1 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <AlertTitle className="text-lg font-bold text-rose-900 dark:text-rose-100 m-0">
                  Pendaftaran Ditutup
                </AlertTitle>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-rose-200 text-rose-800 dark:bg-rose-800 dark:text-rose-200 w-fit">
                  Jalur {dataPeriode?.nama_jalur_jenis}
                </span>
              </div>

              <AlertDescription className="text-slate-700 dark:text-slate-300 text-sm">
                <div className="grid gap-3">
                  {/* Info Timeline Grid */}
                  <div className="bg-white/60 dark:bg-black/20 p-3 rounded-lg border border-rose-100 dark:border-rose-900/50 grid sm:grid-cols-2 gap-4">
                    <div className="flex gap-2.5 items-start">
                      <CalendarClock className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
                      <div className="flex flex-col">
                        <span className="text-xs text-rose-700/80 dark:text-rose-300/80">
                          Batas Pendaftaran & Validasi
                        </span>
                        <span className="font-semibold text-rose-900 dark:text-rose-100">
                          {dataPeriodeActive?.waktu_selesai_daftar_valid
                            ? formatDateIndonesia(
                                dataPeriodeActive.waktu_selesai_daftar_valid,
                              )
                            : "-"}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2.5 items-start">
                      <Megaphone className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
                      <div className="flex flex-col">
                        <span className="text-xs text-rose-700/80 dark:text-rose-300/80">
                          Pengumuman Kelulusan
                        </span>
                        <span className="font-semibold text-rose-900 dark:text-rose-100">
                          {dataPeriode?.waktu_pengumuman_kelulusan
                            ? formatDateIndonesia(
                                dataPeriode.waktu_pengumuman_kelulusan,
                              )
                            : "-"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Upselling / Info Tambahan */}
                  <div className="flex items-center justify-between gap-3 text-sm bg-amber-50 dark:bg-amber-900/10 p-3 rounded-lg border border-amber-100 dark:border-amber-800/50">
                    <span className="text-amber-800 dark:text-amber-200">
                      Ingin mendaftar di jalur lain?
                    </span>
                    <Link to="/dashboard">
                      <Button
                        variant="link"
                        className="h-auto p-0 text-amber-700 font-bold hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-300"
                      >
                        Cek Dashboard <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {/* --- BAGIAN 2: TELEGRAM CTA (SELALU MUNCUL) --- */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 border border-blue-100 dark:border-blue-800 rounded-xl p-4 sm:p-5 shadow-sm mb-8">
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left">
          <div className="p-3 bg-white dark:bg-blue-900/50 rounded-full shadow-sm text-blue-600 dark:text-blue-400 shrink-0">
            <MessageCircle className="h-6 w-6" />
          </div>

          <div className="flex-1 space-y-1">
            <h4 className="text-base font-bold text-blue-900 dark:text-blue-100">
              Grup Telegram Resmi SMBJM {dataPeriode?.tahun}
            </h4>
            <p className="text-sm text-blue-700/80 dark:text-blue-200/80 max-w-2xl">
              Dapatkan notifikasi pengumuman, jadwal ujian, dan informasi
              penting lainnya secara <i>real-time</i> langsung dari panitia.
            </p>
          </div>

          <Link
            to={web.linkGrupTelegramSMBJM}
            target="_blank"
            className="shrink-0 w-full sm:w-auto"
          >
            <Button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200/50 dark:shadow-none transition-all hover:scale-105">
              Gabung Sekarang <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {/* Dekorasi Background */}
        <div className="absolute -top-6 -right-6 w-24 h-24 bg-blue-200/20 rounded-full blur-2xl" />
        <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-indigo-200/20 rounded-full blur-3xl" />
      </div>
      <br />
      <div className="flex lg:flex-col flex-row lg:gap-10">
        <Stepsbar
          steps={steps}
          goToStep={goToStep}
          isPending={isPendingSteps}
        />
        <div className="md:ml-0 ml-5 flex-1">
          <div className="flex lg:flex-row flex-col lg:gap-5 gap-3">
            {SHOW_TIMELINE && (
              <div>
                <TimelinePenerimaan />
              </div>
            )}

            <div className="flex-1">
              {currentStep}

              {/* Wrapper Navigation */}
              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 flex flex-col-reverse sm:flex-row justify-between items-center gap-4">
                {/* Tombol KIRI (Kembali) */}
                <div className="w-full sm:w-auto">
                  {!firstStep && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        backStep();
                        window.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }}
                      className="w-full sm:w-auto text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Kembali
                    </Button>
                  )}
                </div>

                {/* Tombol KANAN (Group Aksi) */}
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  {/* Tombol Simpan (Hanya di step awal & bisa diedit) */}
                  {firstStep && !isDisableInput && (
                    <Button
                      type="button"
                      variant="secondary" // Menggunakan varian secondary agar tidak rebutan fokus dengan 'Berikutnya'
                      className="w-full sm:w-auto bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800"
                      disabled={isPendingSaveBiodata}
                      onClick={handleSaveBiodata}
                    >
                      {isPendingSaveBiodata ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Menyimpan...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Simpan
                        </>
                      )}
                    </Button>
                  )}

                  {/* Tombol Berikutnya */}
                  {!lastStep &&
                    (!firstStep || (firstStep && dataStep?.data_diri == 1)) && (
                      <Button
                        variant="default"
                        type="button"
                        className={`flex-1 sm:flex-none ${currentStepNumber == 6 && dataStep?.data_portofolio != 1 ? "hidden" : ""}`}
                        disabled={
                          isPendingSaveBiodata ||
                          currentIndex === currentIndexDisable - 1
                        }
                        onClick={handleNextStep}
                      >
                        Berikutnya
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
